import { format, parseISO } from 'date-fns'

import type { ChargeWindow } from '../useBusinessQueries'
import { type AdminCharges, toCents } from './chargeSummary'

/** Calendar key of a UTC period start, so labels never shift a day west of UTC. */
export function periodKey(start: string, window: ChargeWindow): string {
  return window === 'MONTH' ? start.slice(0, 7) : start.slice(0, 10)
}

const sameYear = (from: string, to: string) =>
  from.slice(0, 4) === to.slice(0, 4)

/** "Oct 4 – Nov 3, 2026", with both years when the range crosses one. */
export function formatPeriodRange(from: string, to: string): string {
  const start = parseISO(from.slice(0, 10))
  const end = parseISO(to.slice(0, 10))
  return sameYear(from, to)
    ? `${format(start, 'MMM d')} – ${format(end, 'MMM d, yyyy')}`
    : `${format(start, 'MMM d, yyyy')} – ${format(end, 'MMM d, yyyy')}`
}

/** Axis label of a period; the range cuts the first and last ones, so they are marked partial. */
export function periodLabel(
  key: string,
  window: ChargeWindow,
  range: { from: string; to: string },
): string {
  const date = parseISO(key)
  const label =
    window === 'MONTH'
      ? format(date, 'MMM yyyy')
      : format(date, sameYear(range.from, range.to) ? 'MMM d' : 'MMM d, yyyy')
  const partial =
    key === periodKey(range.from, window) || key === periodKey(range.to, window)
  return partial ? `${label} (partial)` : label
}

export interface FlowPoint {
  key: string
  series: 'Charged' | 'Credited, all accounts'
  value: number
}

/** Credited and charged dollars per period of the charges range, summed in cents. */
export function balanceFlow(
  charges: AdminCharges,
  credited: { day: string; value: number }[],
  window: ChargeWindow,
): FlowPoint[] {
  const first = periodKey(charges.from, window)
  const totals = new Map<string, { charged: number; credited: number }>()
  const at = (key: string) => {
    const entry = totals.get(key) ?? { charged: 0, credited: 0 }
    totals.set(key, entry)
    return entry
  }
  for (const bucket of charges.buckets) {
    at(periodKey(bucket.periodStart, window)).charged += toCents(
      bucket.amountUsd,
    )
  }
  for (const point of credited) {
    const key = periodKey(point.day, window)
    if (key >= first) at(key).credited += point.value
  }
  return [...totals]
    .sort(([a], [b]) => a.localeCompare(b))
    .flatMap(([key, entry]) => [
      { key, series: 'Charged' as const, value: entry.charged / 100 },
      {
        key,
        series: 'Credited, all accounts' as const,
        value: entry.credited / 100,
      },
    ])
}
